// Class profiles: describe each class once, then every adaptation starts from it.
import { useEffect, useState, type FormEvent } from "react";
import { describeParams, DEFAULT_PARAMS } from "../../shared/lesson-model";
import { api, ApiError } from "../lib/api";
import { Link } from "../lib/router";
import { useSession } from "../lib/session";
import { useToast } from "../lib/toast";
import type { Profile } from "../lib/types";
import { Dials } from "../components/Dials";
import { SignInPanel } from "../components/SignInPanel";

const { includeKit: _k, ...BLANK } = DEFAULT_PARAMS;
type Draft = { id?: string; name: string; params: Profile["params"] };

export default function Profiles() {
  const { me, loading } = useSession();
  const toast = useToast();
  const [items, setItems] = useState<Profile[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (me?.user) api.profiles().then((r) => setItems(r.profiles)).catch((e: ApiError) => toast(e.message, "error"));
  }, [me?.user, toast]);

  if (loading) return <div className="page narrow"><p className="muted">Loading…</p></div>;
  if (!me?.user) {
    return (
      <div className="page narrow">
        <h1>Class profiles</h1>
        <p>Describe each of your classes once — level, size, setting, first language, interests — and adapt any lesson for them in one click.</p>
        <SignInPanel />
      </div>
    );
  }

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setBusy(true);
    try {
      if (draft.id) {
        const { profile } = await api.updateProfile(draft.id, draft.name, draft.params);
        setItems((xs) => xs?.map((x) => (x.id === profile.id ? profile : x)) ?? null);
      } else {
        const { profile } = await api.createProfile(draft.name, draft.params);
        setItems((xs) => [profile, ...(xs ?? [])]);
      }
      setDraft(null);
      toast("Class profile saved");
    } catch (err) {
      toast((err as ApiError).message, "error");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (p: Profile) => {
    if (!confirm(`Delete the class profile “${p.name}”?`)) return;
    await api.deleteProfile(p.id);
    setItems((xs) => xs?.filter((x) => x.id !== p.id) ?? null);
    if (localStorage.getItem("las.activeProfile") === p.id) localStorage.removeItem("las.activeProfile");
  };
  const use = (p: Profile) => {
    localStorage.setItem("las.activeProfile", p.id);
    toast(`“${p.name}” will be applied in the Studio`);
  };

  const limit = me.access.maxProfiles;
  const atLimit = (items?.length ?? 0) >= limit;
  return (
    <div className="page narrow-wide">
      <h1>Class profiles</h1>
      <p className="lead">Each profile pre-fills the Studio dials. Pick a lesson, and it's already set up for that class.</p>

      {draft ? (
        <form className="profile-form" onSubmit={(e) => void save(e)}>
          <label className="dial-label" htmlFor="pn">Class name</label>
          <input id="pn" required maxLength={60} value={draft.name} placeholder="e.g. Form 4 Dahlia (Tuesday double)" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <Dials value={draft.params} onChange={(v) => setDraft({ ...draft, params: v })} />
          <div className="actions-row">
            <button className="btn btn-primary" disabled={busy}>{busy ? "Saving…" : "Save profile"}</button>
            <button type="button" className="btn btn-quiet" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </form>
      ) : (
        <div className="actions-row">
          <button className="btn btn-primary" disabled={atLimit} onClick={() => setDraft({ name: "", params: { ...BLANK } })}>New class profile</button>
          {atLimit && <span className="small muted">Free accounts keep {limit} profiles. <Link to="/pricing">Subscribers get unlimited.</Link></span>}
        </div>
      )}

      {items === null ? <p className="muted">Loading…</p> : items.length === 0 && !draft ? (
        <p className="empty">No profiles yet.</p>
      ) : (
        <ul className="profiles">
          {items.map((p) => (
            <li key={p.id} className="profile">
              <div>
                <p className="profile-name">{p.name}</p>
                <p className="muted small">{describeParams({ ...p.params, includeKit: false })}</p>
              </div>
              <div className="actions-row">
                <button className="btn btn-quiet" onClick={() => use(p)}>Use in Studio</button>
                <button className="btn btn-quiet" onClick={() => setDraft({ id: p.id, name: p.name, params: p.params })}>Edit</button>
                <button className="btn btn-quiet" onClick={() => void remove(p)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
